/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
  SolanaError,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlySignerAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
} from '@solana/kit';
import {
  getAccountMetaFactory,
  type ResolvedInstructionAccount,
} from '@solana/program-client-core';
import { CPMM_PROGRAM_ADDRESS } from '../programs';

export const SET_HOOK_DISCRIMINATOR = new Uint8Array([
  175, 16, 187, 252, 19, 54, 111, 221,
]);

export function getSetHookDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(SET_HOOK_DISCRIMINATOR);
}

export type SetHookInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountConfig extends string | AccountMeta<string> = string,
  TAccountPool extends string | AccountMeta<string> = string,
  TAccountAdmin extends string | AccountMeta<string> = string,
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountConfig extends string
        ? ReadonlyAccount<TAccountConfig>
        : TAccountConfig,
      TAccountPool extends string
        ? WritableAccount<TAccountPool>
        : TAccountPool,
      TAccountAdmin extends string
        ? ReadonlySignerAccount<TAccountAdmin> &
            AccountSignerMeta<TAccountAdmin>
        : TAccountAdmin,
      ...TRemainingAccounts,
    ]
  >;

export type SetHookInstructionData = {
  discriminator: ReadonlyUint8Array;
  hookProgram: Address;
  hookFlags: number;
};

export type SetHookInstructionDataArgs = {
  hookProgram: Address;
  hookFlags: number;
};

export function getSetHookInstructionDataEncoder(): FixedSizeEncoder<SetHookInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['hookProgram', getAddressEncoder()],
      ['hookFlags', getU32Encoder()],
    ]),
    (value) => ({ ...value, discriminator: SET_HOOK_DISCRIMINATOR }),
  );
}

export function getSetHookInstructionDataDecoder(): FixedSizeDecoder<SetHookInstructionData> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['hookProgram', getAddressDecoder()],
    ['hookFlags', getU32Decoder()],
  ]);
}

export function getSetHookInstructionDataCodec(): FixedSizeCodec<
  SetHookInstructionDataArgs,
  SetHookInstructionData
> {
  return combineCodec(
    getSetHookInstructionDataEncoder(),
    getSetHookInstructionDataDecoder(),
  );
}

export type SetHookInput<
  TAccountConfig extends string = string,
  TAccountPool extends string = string,
  TAccountAdmin extends string = string,
> = {
  config: Address<TAccountConfig>;
  pool: Address<TAccountPool>;
  admin: TransactionSigner<TAccountAdmin>;
  hookProgram: SetHookInstructionDataArgs['hookProgram'];
  hookFlags: SetHookInstructionDataArgs['hookFlags'];
};

export function getSetHookInstruction<
  TAccountConfig extends string,
  TAccountPool extends string,
  TAccountAdmin extends string,
  TProgramAddress extends Address = typeof CPMM_PROGRAM_ADDRESS,
>(
  input: SetHookInput<TAccountConfig, TAccountPool, TAccountAdmin>,
  config?: { programAddress?: TProgramAddress },
): SetHookInstruction<
  TProgramAddress,
  TAccountConfig,
  TAccountPool,
  TAccountAdmin
> {
  // Program address.
  const programAddress = config?.programAddress ?? CPMM_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    config: { value: input.config ?? null, isWritable: false },
    pool: { value: input.pool ?? null, isWritable: true },
    admin: { value: input.admin ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedInstructionAccount
  >;

  // Original args.
  const args = { ...input };

  const getAccountMeta = getAccountMetaFactory(programAddress, 'programId');
  return Object.freeze({
    accounts: [
      getAccountMeta('config', accounts.config),
      getAccountMeta('pool', accounts.pool),
      getAccountMeta('admin', accounts.admin),
    ],
    data: getSetHookInstructionDataEncoder().encode(
      args as SetHookInstructionDataArgs,
    ),
    programAddress,
  } as SetHookInstruction<
    TProgramAddress,
    TAccountConfig,
    TAccountPool,
    TAccountAdmin
  >);
}

export type ParsedSetHookInstruction<
  TProgram extends string = typeof CPMM_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    config: TAccountMetas[0];
    pool: TAccountMetas[1];
    admin: TAccountMetas[2];
  };
  data: SetHookInstructionData;
};

export function parseSetHookInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedSetHookInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 3) {
    throw new SolanaError(
      SOLANA_ERROR__PROGRAM_CLIENTS__INSUFFICIENT_ACCOUNT_METAS,
      {
        actualAccountMetas: instruction.accounts.length,
        expectedAccountMetas: 3,
      },
    );
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      config: getNextAccount(),
      pool: getNextAccount(),
      admin: getNextAccount(),
    },
    data: getSetHookInstructionDataDecoder().decode(instruction.data),
  };
}
